import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from './contexts/GlobalContext.jsx';
import {
  defaultCatchValidation,
  getSummaryActionPlans,
} from '../services/task-tracking.js';
import {
  Breadcrumb,
  Button,
  Divider,
  Select,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { HomeFilled } from '@ant-design/icons';
import LoadSpinner from './shared/LoadSpinner.jsx';
import { getSessionScopeVars, getTreeKeys } from '../utils/fns.js';
import ExpandCollapseButton from './shared/ExpandCollapseButton.jsx';
import { AppRoutes } from '../utils/app-routes.js';
import IconExportXLSX from '../images/export-xlsx.ico';
import ReportUsageTracker from './shared/ReportUsageTracker.jsx';
import { ProjectFilterOptionAll } from '../utils/constants.js';

let ac = new AbortController();

export default function SummaryActionPlansScreen() {
  const navigate = useNavigate();
  const { session, updateSession } = useContext(GlobalContext);

  const [loading, setLoading] = useState(true);

  const [tableData, setTableData] = useState([]);

  const [rowKeys, setRowKeys] = useState([]);
  const [expRowKeys, setExpRowKeys] = useState([]);
  const [projectFilterOptions, setProjectFilterOptions] = useState([
    ProjectFilterOptionAll,
  ]);
  const [projectFilter, setProjectFilter] = useState(
    ProjectFilterOptionAll.value
  );

  useEffect(() => {
    if (session.accessToken === undefined) {
      navigate(AppRoutes.Logout);
      return;
    }

    updateSession({
      qsScopeType: '',
      qsScopeKeys: '',
      qsArray: null,
    });

    fetchData(projectFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchData = (projectFilter) => {
    setLoading(true);

    ac.abort();
    ac = new AbortController();

    const qsArray =
      projectFilter && projectFilter !== ProjectFilterOptionAll.value
        ? [{ id: 'projectID', value: projectFilter }]
        : null;

    getSummaryActionPlans(ac, session.accessToken, qsArray)
      .then((json) => {
        const summaryTreeRowKeys = getTreeKeys(json.summaryTree);
        setRowKeys(summaryTreeRowKeys);
        setExpRowKeys(summaryTreeRowKeys); // Expand all by default
        setTableData(json.summaryTree);
        const optionMap = {};
        json.projects.forEach(({ id, name }) => {
          optionMap[id] = { value: id, label: name };
        });

        const options = [
          ProjectFilterOptionAll,
          ...Object.values(optionMap).sort((a, b) =>
            `${b.value}`.localeCompare(`${a.value}`)
          ),
        ];
        setProjectFilterOptions(options);
      })
      .catch((json) => {
        defaultCatchValidation(json, navigate);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns = [
    {
      title: (
        <>
          <ExpandCollapseButton
            availableExpand={expRowKeys.length < rowKeys.length}
            onExpand={() => {
              setExpRowKeys(rowKeys);
            }}
            expandText="Desplegar todo"
            onCollapse={() => {
              setExpRowKeys([]);
            }}
            collapseText="Reducir todo"
          />
          Alcance del plan
        </>
      ),
      dataIndex: 'name',
      key: 'name',
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => `${a.name}`.localeCompare(`${b.name}`),
      render: (name, record) => {
        const title = `${record.sortWeight > 1 ? 'Tipo de alcance: ' : ''}${name}`;
        return (
          <Button
            className={`py-0 px-2 ${record.sortWeight > 1 ? 'has-text-weight-bold' : 'is-italic'}`}
            size="small"
            type="link"
            shape="rounded"
            onClick={() => {
              updateSession(getSessionScopeVars(record.scopeMetadata));
              navigate(AppRoutes.ActionPlanList);
            }}
          >
            {record.sortWeight === 0 ? (
              <Tooltip
                title={record.scopeMetadata.scopeKeysLabel}
                placement="right"
              >
                {title}
              </Tooltip>
            ) : (
              <>{title}</>
            )}
          </Button>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: ['summary', 'total'],
      key: 'total',
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => a.summary.total - b.summary.total,
      render: (total) => <label>{total}</label>,
    },
    {
      title: 'Promedio de avance',
      dataIndex: ['summary', 'avgProgress'],
      key: 'avgProgress',
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => a.summary.avgProgress - b.summary.avgProgress,
      render: (avgProgress) => <label>{avgProgress}%</label>,
    },
    {
      title: 'No expirados',
      dataIndex: ['summary', 'active'],
      key: 'active',
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => a.summary.active.current - b.summary.active.current,
      render: ({ current, percentage }) => (
        <label>{`${current} (${percentage}%)`}</label>
      ),
    },
    {
      title: 'Expirados',
      dataIndex: ['summary', 'expired'],
      key: 'expired',
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => a.summary.expired.current - b.summary.expired.current,
      render: ({ current, percentage }) => (
        <label>{`${current} (${percentage}%)`}</label>
      ),
    },
    {
      title: 'Cerrados',
      dataIndex: ['summary', 'closed'],
      key: 'closed',
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => a.summary.closed.current - b.summary.closed.current,
      render: ({ current, percentage }) => (
        <label>{`${current} (${percentage}%)`}</label>
      ),
    },
  ];

  const buildRows = (nodes, level = 0) => {
    if (!nodes) return [];

    return nodes.flatMap((node) => {
      const { active, total, closed, expired, avgProgress } = node.summary;
      const row = {
        'Alcance del plan': `${' '.repeat(level * 4)}${node.name}`,
        Total: total || 0,
        'Promedio de avance': avgProgress || 0,
        Activos: `${active?.current} (${active?.percentage}%)` || 0,
        Expirados: `${expired?.current} (${expired?.percentage})%` || 0,
        Cerrados: `${closed?.current} (${closed?.percentage})%` || 0,
      };

      const children = buildRows(node.children, level + 1);
      return [row, ...children];
    });
  };

  const exportSummaryToExcel = async (summaryTree) => {
    const XLSX = await import('xlsx');
    const rows = buildRows(summaryTree);

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Resumen');

    XLSX.writeFile(workbook, 'summary-action-plans.xlsx');
  };

  return (
    <div className="action-plan-list-screen-container weglot-translate">
      {loading ? (
        <LoadSpinner />
      ) : (
        <div>
          <div className="is-flex is-align-items-center is-justify-content-space-between">
            <Breadcrumb
              className="is-breadcrumb"
              separator=">"
              items={[{ title: <HomeFilled /> }]}
            />
            <img
              className="m-2 is-clickable"
              src={IconExportXLSX}
              alt="Exportar planes de acción"
              height="30"
              width="30"
              onClick={() => exportSummaryToExcel(tableData)}
            />
          </div>
          <div className="box break-on-print">
            <h4 className="mb-4">Resumen de Planes de acción</h4>

            <div>
              <Typography.Text strong className="is-block">
                Medición donde se creó:
              </Typography.Text>
              <Select
                showSearch
                style={{ width: 300 }}
                value={projectFilter}
                onChange={(value) => {
                  setProjectFilter(value);
                  fetchData(value);
                }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                notFoundContent={null}
                options={projectFilterOptions}
              />
            </div>
            <Divider />
            <Table
              size="small"
              showSorterTooltip={false}
              expandable={{
                expandedRowKeys: expRowKeys,
                onExpandedRowsChange: (expRows) => {
                  setExpRowKeys(expRows);
                },
              }}
              dataSource={tableData}
              columns={columns}
              pagination={false}
              scroll={{ x: true }}
            />
            <ReportUsageTracker
              userToken={session.userToken}
              customerCode={session.customerCode}
              reportID={73}
            />
          </div>
        </div>
      )}
    </div>
  );
}
